import React, { useState, useRef, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import TicketMessageItem from './TicketMessageItem';
import { ConversationContextModal } from './ConversationContextModal';
import ChatImageLightbox from './ChatImageLightbox';

const TicketMessagesStream = ({
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore,
    error,
    onReply,
    onOpenLightbox: propOnOpenLightbox
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [contextMessageId, setContextMessageId] = useState(null);
    const [contextFromNumber, setContextFromNumber] = useState(null);
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);
    const streamContainerRef = useRef(null);
    const prevScrollHeightRef = useRef(0);
    const isPrependRef = useRef(false);
    const isAtBottomRef = useRef(true);
    const lastMsgIdRef = useRef(null);

    // Estados para el botón flotante y el contador de mensajes nuevos
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const [internalLightboxImage, setInternalLightboxImage] = useState(null);
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);

    const handleScrollToMessage = (targetId) => {
        const el = document.getElementById(`ticket-msg-${targetId}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setHighlightedMessageId(targetId);
            setTimeout(() => {
                setHighlightedMessageId(null);
            }, 1800);
        }
    };

    const handleOpenContext = (idMensaje, fromNumber = null) => {
        setContextMessageId(idMensaje);
        setContextFromNumber(fromNumber);
        setIsContextModalOpen(true);
    };

    const handleOpenLightbox = (url) => {
        if (propOnOpenLightbox) {
            propOnOpenLightbox(url);
        } else {
            setInternalLightboxImage(url);
        }
    };

    // Manejo del scroll ante cambios en la lista de mensajes
    useEffect(() => {
        const container = streamContainerRef.current;
        if (!container) return;

        if (isPrependRef.current) {
            const diff = container.scrollHeight - prevScrollHeightRef.current;
            container.scrollTop = diff;
            isPrependRef.current = false;
        } else {
            const lastMsg = messages[messages.length - 1];
            const lastId = lastMsg?.idMensaje || lastMsg?.id;
            const prevLastId = lastMsgIdRef.current;

            // Si es la carga inicial o el usuario está al fondo del chat
            if (isAtBottomRef.current || !prevLastId) {
                container.scrollTop = container.scrollHeight;
                setShowScrollBottom(false);
                setUnreadCount(0);
            } else if (lastId && lastId !== prevLastId) {
                // Buscamos dónde estaba el último mensaje que teníamos
                const prevIndex = messages.findIndex(m => (m.idMensaje || m.id) === prevLastId);

                if (prevIndex !== -1) {
                    // Tomamos únicamente los mensajes que cayeron AL FONDO después de ese
                    const newlyArrived = messages.slice(prevIndex + 1);

                    // Filtramos que sean del cliente (para no sumar lo que tú mismo envíes)
                    const clientNew = newlyArrived.filter(m => !m.bEsAgente && m.bEsAgente !== 1);

                    if (clientNew.length > 0) {
                        setUnreadCount(prev => prev + clientNew.length);
                        setShowScrollBottom(true);
                    }
                }
            }

            // Actualizamos el ID del último mensaje
            lastMsgIdRef.current = lastId;
        }
    }, [messages]);

    // Infinite scroll automático hacia arriba al llegar cerca del tope
    const handleScroll = () => {
        const container = streamContainerRef.current;
        if (!container) return;

        // Detectar si el usuario está cerca del fondo (margen de 80px)
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const isNearBottom = distanceFromBottom < 80;
        isAtBottomRef.current = isNearBottom;

        if (isNearBottom) {
            setShowScrollBottom(false);
            setUnreadCount(0);
        } else {
            setShowScrollBottom(true);
        }

        // Infinite scroll automático hacia arriba al llegar cerca del tope
        if (isLoadingMore || !hasMore || isLoading) return;
        if (container.scrollTop < 60) {
            prevScrollHeightRef.current = container.scrollHeight;
            isPrependRef.current = true;
            onLoadMore();
        }
    };

    // Función al hacer click en la flecha flotante
    const scrollToBottom = () => {
        const container = streamContainerRef.current;
        if (!container) return;

        container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
        });
        setUnreadCount(0);
        setShowScrollBottom(false);
    };

    return (
        <div className="position-relative flex-grow-1 d-flex flex-column overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
            {/* Contenedor principal con scroll vertical */}
            <div
                ref={streamContainerRef}
                onScroll={handleScroll}
                className={`flex-grow-1 p-2 p-sm-3 p-md-4 d-flex flex-column gap-3 se-custom-scrollbar ${isDark ? 'se-dark-scrollbar' : ''}`}
                style={{
                    backgroundColor: isDark ? '#0b141a' : '#ffffff',
                    height: '100%',
                    minHeight: 0,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: isDark ? '#2b394a transparent' : '#cbd5e1 transparent'
                }}
            >
                {/* Botón / Spinner de carga para mensajes anteriores */}
                {isLoadingMore && (
                    <div className="text-center text-muted my-2" style={{ fontSize: '0.8rem' }}>
                        <div className="spinner-border spinner-border-sm me-2 text-success" role="status"></div>
                        Cargando mensajes anteriores...
                    </div>
                )}

                {hasMore && !isLoadingMore && !isLoading && messages.length >= 30 && (
                    <div className="text-center my-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-muted text-decoration-none shadow-none"
                            style={{ fontSize: '0.75rem' }}
                            onClick={onLoadMore}
                        >
                            ▲ Cargar mensajes anteriores
                        </button>
                    </div>
                )}

                {isLoading && (
                    <div className="text-center text-muted my-3" style={{ fontSize: '0.85rem' }}>
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Cargando mensajes...
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger mx-3 my-2 text-center" style={{ fontSize: '0.85rem' }} role="alert">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                        {error}
                    </div>
                )}

                {!isLoading && !error && messages.length === 0 && (
                    <div className="text-center text-muted my-3" style={{ fontSize: '0.85rem' }}>
                        No hay mensajes en este ticket aún.
                    </div>
                )}

                {/* Centered Date Tag */}
                {messages.length > 0 && (
                    <div className="text-center my-1">
                        <span
                            className="px-3 py-1 rounded-pill fw-semibold"
                            style={{
                                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                                color: isDark ? '#94a3b8' : '#64748b',
                                fontSize: '0.72rem'
                            }}
                        >
                            Historial de Conversación
                        </span>
                    </div>
                )}

                {/* Lista de Mensajes */}
                {messages.map((msg, index) => (
                    <TicketMessageItem
                        key={msg.idMensaje || msg.id || index}
                        msg={msg}
                        index={index}
                        messages={messages}
                        onOpenLightbox={handleOpenLightbox}
                        onReply={onReply}
                        onOpenContext={handleOpenContext}
                        onScrollToMessage={handleScrollToMessage}
                        isHighlighted={highlightedMessageId === (msg.idMensaje || msg.id)}
                    />
                ))}
            </div>

            {/* Modal Lightbox Viewer (fallback en caso de no delegarse a columna superior) */}
            {internalLightboxImage && (
                <ChatImageLightbox
                    imageUrl={internalLightboxImage}
                    onClose={() => setInternalLightboxImage(null)}
                />
            )}

            <ConversationContextModal
                isOpen={isContextModalOpen}
                onClose={() => {
                    setIsContextModalOpen(false);
                    setContextFromNumber(null);
                }}
                messageId={contextMessageId}
                fromNumber={contextFromNumber}
            />

            {/* Botón flotante estilo WhatsApp con flecha y contador de mensajes nuevos */}
            {showScrollBottom && (
                <div
                    className="position-absolute"
                    style={{
                        bottom: '20px',
                        right: '24px',
                        zIndex: 10
                    }}
                >
                    <button
                        type="button"
                        onClick={scrollToBottom}
                        className="btn position-relative rounded-circle shadow-lg d-flex align-items-center justify-content-center border-0 p-0"
                        style={{
                            width: '42px',
                            height: '42px',
                            backgroundColor: isDark ? '#202c33' : '#ffffff',
                            color: isDark ? '#aebac1' : '#54656f',
                            boxShadow: isDark
                                ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                                : '0 4px 12px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            transition: 'transform 0.15s ease, background-color 0.15s ease'
                        }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        title="Ir al último mensaje"
                    >
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.85rem' }} />

                        {/* Badge verde con la cantidad de nuevos mensajes */}
                        {unreadCount > 0 && (
                            <span
                                className="position-absolute top-0 start-50 translate-middle badge rounded-pill"
                                style={{
                                    backgroundColor: '#25d366',
                                    color: '#ffffff',
                                    fontSize: '0.68rem',
                                    fontWeight: '600',
                                    padding: '0.22em 0.5em',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.25)'
                                }}
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default TicketMessagesStream;